import React, { useEffect, useState, useId } from 'react'
import {
   AccordionItemProvider,
   AccordionProvider,
   useAccordionContext,
   useAccordionItemContext
} from './AccordionContext';
import './Accordion.scss';
import { Icon } from '../icons/icons';

type WithChildren = {
   children: React.ReactNode
}
type AccordionProps = WithChildren;
type AccordionItemProps = WithChildren & {
   id: string | null
}
type AccordionTriggerProps = WithChildren & {
   headingLevel: number,
   color:string,
   action?: React.ReactNode
};
type AccordionContentProps = WithChildren & {
   color:string
}

/**
 * 
 * @param children: React.ReactNode 
 */
export const Accordion = ({
   children
}: AccordionProps) => {
   return (
      <AccordionProvider >
         {children}
      </AccordionProvider>
   )
}

/**
 * 
 * @param children: React.ReactNode
 * @param id: string | null  
 */
export const AccordionItem = ({
   children,
   id,
}: AccordionItemProps) => {
   return (
      <>
         <AccordionItemProvider id={id} >
            {children}
         </AccordionItemProvider>
      </>
   )
}

/**
 * 
 * @param children: React.ReactNode 
 */
export const AccordionTrigger = ({ children, headingLevel, color, action }: AccordionTriggerProps) => {
   const context = useAccordionContext();
   const item = useAccordionItemContext();
   const [isOpen, setIsOpen] = useState(false);
   const [isHovered, setIsHovered] = useState(false);
   const HeadingTag = `h${headingLevel}` as keyof JSX.IntrinsicElements;


   useEffect(() => {
      const activeContent = context?.activeContent === item?.id;
      setIsOpen(activeContent);
   }, [context]);


   // context can be null
   if (!context || !item) return null
   return (
      <HeadingTag
         className={'accordion-button-container ' + color + (isOpen ? ' open' : '') + (isHovered ? ' hovered' : '')}
         onMouseOver={() => setIsHovered(true)}
         onMouseOut={() => setIsHovered(false)}
      >
         <button
            className={'accordion-button'}
            aria-expanded={isOpen}
            id={'accordionButton' + item?.id}
            aria-controls={'accordionContent' + item?.id}
            onClick={() => context?.handleTrigger(item?.id)}
         >
            {
               isOpen ? (
                  <Icon className={'btn-icon icon-' + color + ' accordion-button-icon ' + (isHovered ? 'hover' : '')} name='icon-minus' />

               ) : <Icon className={'btn-icon icon-' + color + ' accordion-button-icon ' + (isHovered ? 'hover' : '')} name='icon-plus' />
            }
            <span className={'accordion-button-title ' + (isHovered ? 'hover' : '')}>{children}</span>
         </button>
         {action && (
            <div className={'accordion-button-action' + (isHovered ? ' hover' : '')} onClick={e => e.stopPropagation()}>
               {action}
            </div>
         )}
      </HeadingTag>
   );
}

/**
 * 
 * @param children: React.ReactNode
 * @returns 
 */
export const AccordionContent = ({ children, color }: AccordionContentProps) => {
   const context = useAccordionContext();
   const item = useAccordionItemContext();
   const [isOpen, setIsOpen] = useState(false);

   useEffect(() => {
      const activeContent = context?.activeContent === item?.id;
      setIsOpen(activeContent);
   }, [context]);
   return (
      <>
         {
            isOpen ? (
               <div className={'accordion-content ' + color + (isOpen ? ' open' : '')} id={'accordionContent' + item?.id} role='region' aria-labelledby={'accordionButton' + item?.id}>
                  {children}
               </div>
            ) : null
         }
      </>
   )
}